import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { passwordResetAPI } from '../services/api';
import '../components/ForgotPassword.css';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setError('Enlace inválido. Faltan parámetros requeridos.');
        setIsValidating(false);
        return;
      }

      try {
        await passwordResetAPI.validateToken(token, email);
        setTokenValid(true);
      } catch (err: any) {
        setError(err.response?.data?.message || 'El enlace es inválido o ha expirado');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, email]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!token || !email) {
      setError('Datos del enlace faltantes');
      return;
    }

    setIsLoading(true);

    try {
      await passwordResetAPI.resetPassword(token, email, formData.password);
      setSuccess(true);
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cambiar la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header">
            <span className="forgot-password-icon">🔄</span>
            <h2>Validando enlace...</h2>
            <p>Por favor espera mientras verificamos tu enlace de recuperación</p>
          </div>
          <div className="forgot-password-content">
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state - invalid token
  if (!tokenValid) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            <span className="forgot-password-icon">❌</span>
            <h2>Enlace Inválido</h2>
            <p>El enlace de recuperación no es válido o ha expirado</p>
          </div>
          
          <div className="forgot-password-content">
            <div className="error-message">
              {error}
            </div>

            <div className="forgot-password-help">
              <h3>¿Qué puedes hacer?</h3>
              <ul>
                <li>Verifica que hayas copiado el enlace completo</li>
                <li>Los enlaces expiran después de 30 minutos</li>
                <li>Cada enlace solo puede usarse una vez</li>
                <li>Solicita un nuevo enlace de recuperación</li>
              </ul>
            </div>

            <div className="forgot-password-actions">
              <Link to="/login" className="btn-secondary">
                ← Volver al Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          <div className="forgot-password-header success">
            <span className="forgot-password-icon">✅</span>
            <h2>¡Contraseña Actualizada!</h2>
            <p>Tu contraseña ha sido cambiada exitosamente</p>
          </div>

          <div className="forgot-password-content">
            <div className="success-message">
              <p>
                Tu contraseña ha sido actualizada correctamente. 
                Serás redirigido al login en unos segundos para que puedas 
                iniciar sesión con tu nueva contraseña.
              </p>
            </div>

            <div className="forgot-password-actions">
              <Link to="/login" className="btn-primary">
                Ir al Login Ahora
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <span className="forgot-password-icon">🔑</span>
          <h2>Nueva Contraseña</h2>
          <p>Introduce tu nueva contraseña para la cuenta: <strong>{email}</strong></p>
        </div>

        {error && (
          <div className="forgot-password-content">
            <div className="error-message">
              {error}
            </div>
          </div>
        )}

        <div className="forgot-password-content">
          <form onSubmit={handleSubmit} className="forgot-password-form">
            <div className="form-group">
              <label htmlFor="password">Nueva Contraseña</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mínimo 6 caracteres"
                required
                autoComplete="new-password"
                autoFocus
                minLength={6}
              />
              <small className="form-help">
                La contraseña debe tener al menos 6 caracteres
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Contraseña</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repite la contraseña"
                required
                autoComplete="new-password"
                minLength={6}
              />
              <small className="form-help">
                Debe coincidir exactamente con la contraseña anterior
              </small>
            </div>

            <div className="forgot-password-actions">
              <Link to="/login" className="btn-secondary">
                ← Cancelar
              </Link>
              
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    Cambiando...
                  </>
                ) : (
                  <>
                    💾 Cambiar Contraseña
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="forgot-password-help">
            <h3>Consejos para tu contraseña:</h3>
            <ul>
              <li>Usa al menos 6 caracteres</li>
              <li>Combina letras, números y símbolos</li>
              <li>No uses información personal</li>
              <li>Haz que sea única y fácil de recordar</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;